import React, { createContext, useCallback, useContext, useEffect, useState } from "react"

import EN from "./en.json"
import LANGUAGE_LIST from "./languages.json"
import { contentKey } from "./contentKey"

// THE SITE'S LANGUAGE [R-020]. Every fixed piece of text on the site is a key in ./en.json, read
// through t('key'); the other 59 languages are static/i18n/<code>.json, fetched only when chosen,
// so a visitor never downloads a language they are not reading.
//
// Text that comes from the CMS (trip names, descriptions, articles) goes through tc(text) instead:
// it is looked up by the fingerprint of its English in static/i18n/content/<code>.json [R-022].
//
// Which language: the one they picked from the flag in the header (remembered in this browser),
// otherwise the first of the browser's own languages the site has, otherwise English. The build
// renders English — the browser switches as soon as the page opens.

export const LANGUAGES = LANGUAGE_LIST.languages
const KNOWN = new Set(LANGUAGES.map((l) => l.code))
const STORAGE_KEY = 'ntm-lang'
// browser tags whose first part is not the code the site uses
const ALIAS = { fil: 'tl', 'zh-hk': 'yue', 'zh-mo': 'yue' }

export const languageByCode = (code) => LANGUAGES.find((l) => l.code === code) || LANGUAGES[0]

export function browserSiteLanguage() {
  if (typeof navigator === 'undefined') return 'en'
  const tags = navigator.languages && navigator.languages.length ? navigator.languages : [navigator.language]
  for (const tag of tags) {
    const low = String(tag || '').toLowerCase()
    const code = ALIAS[low] || ALIAS[low.split('-')[0]] || low.split('-')[0]
    if (KNOWN.has(code)) return code
  }
  return 'en'
}

// the locale the browser's date formatting knows each site language by
const INTL = { yue: 'zh-HK', nan: 'zh-TW', zh: 'zh-CN', haz: 'fa-AF', prs: 'fa-AF', aii: 'syr', cld: 'syr', ksw: 'my', din: 'en-AU', en: 'en-AU' }
export const intlLocale = (code) => INTL[code] || code

// a date in the visitor's language: fmtDate(date, { weekday: 'long', day: 'numeric', month: 'short' })
const makeFmtDate = (code) => (date, opts) => {
  try { return new Intl.DateTimeFormat([intlLocale(code), 'en-AU'], opts).format(new Date(date)) }
  catch (e) { return new Intl.DateTimeFormat('en-AU', opts).format(new Date(date)) }
}

const fill = (text, vars) =>
  vars ? text.replace(/\{(\w+)\}/g, (m, k) => (vars[k] !== undefined ? vars[k] : m)) : text

const catalogues = { en: EN }
const contentCatalogues = { en: {} }

const LanguageContext = createContext({
  lang: 'en', setLang: () => {}, t: (k, v) => fill(EN[k] || k, v), tc: (text) => text, fmtDate: makeFmtDate('en'),
})

export const LanguageProvider = ({ children }) => {
  const [lang, setLangState] = useState('en')
  const [, setLoaded] = useState(0)

  const load = useCallback((code) => {
    const get = (url, into) => (into[code] ? Promise.resolve() : fetch(url)
      .then((r) => (r.ok ? r.json() : {}))
      .then((cat) => { into[code] = cat; setLoaded((n) => n + 1) })
      .catch(() => { into[code] = {} }))
    return Promise.all([get(`/i18n/${code}.json`, catalogues), get(`/i18n/content/${code}.json`, contentCatalogues)])
  }, [])

  const apply = useCallback((code) => {
    const l = languageByCode(code)
    document.documentElement.lang = l.code
    document.documentElement.dir = l.dir
    setLangState(l.code)
    load(l.code)
  }, [load])

  useEffect(() => {
    let saved = null
    try { saved = window.localStorage.getItem(STORAGE_KEY) } catch (e) { /* storage blocked */ }
    apply(saved && KNOWN.has(saved) ? saved : browserSiteLanguage())
  }, [apply])

  const setLang = useCallback((code) => {
    try { window.localStorage.setItem(STORAGE_KEY, code) } catch (e) { /* storage blocked */ }
    apply(code)
  }, [apply])

  // a key missing from the chosen language shows its English, never the bare key
  const t = useCallback((key, vars) => {
    const cat = catalogues[lang] || {}
    return fill(cat[key] || EN[key] || key, vars)
  }, [lang, catalogues[lang]]) // eslint-disable-line react-hooks/exhaustive-deps

  // CMS text: its translation if there is one for exactly this English, else the English itself
  const tc = useCallback((text) => {
    if (lang === 'en' || typeof text !== 'string' || !text.trim()) return text
    const hit = (contentCatalogues[lang] || {})[contentKey(text)]
    if (!hit) return text
    const lead = text.match(/^\s*/)[0]
    const trail = text.match(/\s*$/)[0]
    return lead + hit + trail
  }, [lang, contentCatalogues[lang]]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <LanguageContext.Provider value={{ lang, setLang, t, tc, fmtDate: makeFmtDate(lang) }}>
      {children}
    </LanguageContext.Provider>
  )
}

export const useLanguage = () => useContext(LanguageContext)
export const useT = () => useContext(LanguageContext).t
export const useTC = () => useContext(LanguageContext).tc

// A translated sentence that carries markup: '<b>Free entry</b> when you register' with
// { b: (s) => <b>{s}</b> }. Tags are single words and never nested, which keeps them safe through
// translation; a tag the map does not name is shown as its plain text.
export const withTags = (text, tags) => {
  const out = []
  const re = /<(\w+)>(.*?)<\/\1>/g
  let last = 0
  let m
  let i = 0
  while ((m = re.exec(text))) {
    if (m.index > last) out.push(text.slice(last, m.index))
    const render = tags[m[1]]
    out.push(<React.Fragment key={i++}>{render ? render(m[2]) : m[2]}</React.Fragment>)
    last = re.lastIndex
  }
  if (last < text.length) out.push(text.slice(last))
  return out
}

// Language and country names in the visitor's language, from the browser's own tables — no
// catalogue needed. Falls back to the English name given when the browser does not know the code.
export const useDisplayNames = () => {
  const { lang } = useContext(LanguageContext)
  return React.useMemo(() => {
    const make = (type) => {
      try { return new Intl.DisplayNames([intlLocale(lang), 'en'], { type, fallback: 'none' }) } catch (e) { return null }
    }
    const langs = make('language')
    const regions = make('region')
    const of = (dn, code, fallback) => {
      if (lang === 'en' || !dn || !code) return fallback
      try { return dn.of(code) || fallback } catch (e) { return fallback }
    }
    return {
      language: (code, fallback) => of(langs, code, fallback),
      region: (code, fallback) => of(regions, code, fallback),
      compare: (a, b) => a.localeCompare(b, intlLocale(lang)),
    }
  }, [lang])
}
